import * as React from 'react'
import type { TreeDto } from '@lib/dto/tree.dto'
import type { TreeProblemTypeDto } from '@lib/dto/tree-problem-type.dto'
import type { OrganizationDto } from '@lib/dto/organization.dto'
import type { Season } from '@lib/enums/season.enum'
import { formatMoneyValue, parseMoney } from '@lib/helpers/money'
import { taxOn } from '@lib/helpers/tax'
import { documentTaxRate } from '@lib/helpers/proposal-selection'
import type { TreeWorkChoice } from '@lib/enums/tree-work-choice.enum'
import {
  documentRepairCount,
  documentSiteTrees,
  leadsWithRecommended,
  tallyTrees,
  type TreeTally,
} from './tally'
import { hasLaterCents, siteTreeWork, type SiteTreeWork } from './treeWork'
import { useBid, type BidContext } from './useBid'

/** Another site of the bid, as its draft prices it. */
export interface OtherSite {
  siteId: string | null
  name: string
  cents: number
  /** Some of its trees have no time yet. */
  incomplete: boolean
}

/**
 * The bid's other sites: not part of one, still loading, out of reach, or
 * priced.
 */
export type OtherSites =
  | { state: 'none' }
  | { state: 'loading' }
  | { state: 'unavailable' }
  | { state: 'ready'; sites: OtherSite[] }

/** What one tree comes to, for the top of its sheet. */
export interface TreePrice {
  /** Its next-visit work as the client's page first opens it. */
  cents: number
  choice: TreeWorkChoice
  isOptional: boolean
  /** A removal quoted beside its pruning, for the client to choose instead. */
  removalAlternativeCents: number | null
  /** What its later-season work comes to, and the first such season. */
  later: { season: Season; cents: number } | null
}

export interface TreeBidTotal {
  bidContext: BidContext
  work: SiteTreeWork
  /** This site's next visit, broken down. */
  site: TreeTally
  others: OtherSites
  /** Before tax: this site, plus the other sites once they are known. */
  subtotalCents: number | null
  taxRatePercent: string | null
  taxCents: number | null
  /** The headline: what the client would pay, tax included. */
  totalCents: number | null
  /** The headline is a floor: some trees have no time, or time has no rate. */
  isIncomplete: boolean
  /** No crew rate is set, so time prices nothing. */
  rateIsMissing: boolean
  /** Prices set by hand on this site's trees. */
  handPricedCount: number
  /** Work in later seasons on this site, which the headline leaves out. */
  laterSeasons: { season: Season; trees: number; cents: number }[]
  /** This site's figure alone, which the technician's edits move. */
  siteCents: number
  priceOf: (treeId: string) => TreePrice | null
}

interface Args {
  siteId: string
  trees: readonly TreeDto[]
  problemTypes: readonly TreeProblemTypeDto[]
  organization: OrganizationDto | undefined
  visitSeason: Season | null
  requestedProposalId: string | null
}

function handPricedOn(trees: readonly TreeDto[]): number {
  let count = 0
  for (const tree of trees) {
    if (tree.pruningPriceOverride !== null) count += 1
    if (tree.removalOffered && tree.removalPriceOverride !== null) count += 1
    count += tree.problems.filter((problem) => problem.priceOverride !== null)
      .length
  }
  return count
}

/**
 * What the bid a tree survey belongs to comes to, while it is being edited.
 *
 * This site is priced from the phone, so it moves with every edit and keeps
 * moving without a signal. The bid's other sites come from the draft on the
 * server, which this screen is not changing. The figure is the one a client's
 * page opens on: the next visit, "Everything recommended" when some work is
 * optional, pruning where removal is offered beside it, tax included.
 */
export function useTreeBidTotal(args: Args): TreeBidTotal {
  const bidContext = useBid(args.siteId, 'trees', args.requestedProposalId)
  const { bid, document } = bidContext
  const ratePerHour = args.organization?.treeLaborRatePerHour ?? null

  const work = React.useMemo(
    () =>
      siteTreeWork(args.trees, {
        problemTypes: args.problemTypes,
        ratePerHour,
        visitSeason: args.visitSeason,
        onlySeason: bid?.proposal.season ?? null,
      }),
    [args.trees, args.problemTypes, ratePerHour, args.visitSeason, bid?.proposal.season],
  )

  const otherDocumentSites = React.useMemo(() => {
    if (bid === null || document.data === undefined) return null
    const names = new Map(
      bid.version.sites.map((site) => [site.name, site.siteId]),
    )
    return {
      sites: documentSiteTrees(document.data)
        .filter((site) => names.get(site.siteName) !== args.siteId)
        .map((site) => ({ ...site, siteId: names.get(site.siteName) ?? null })),
      repairCount: documentRepairCount(document.data),
      taxRatePercent: documentTaxRate(document.data),
    }
  }, [bid, document.data, args.siteId])

  /*
   * Whether optional work is left out of the headline is a rule about the
   * whole bid, so it is decided over every site's trees at once.
   */
  const countOptional = !leadsWithRecommended(
    [
      ...work.next.map((tree) => tree.row),
      ...(otherDocumentSites?.sites.flatMap((site) =>
        site.trees.map((tree) => tree.row),
      ) ?? []),
    ],
    otherDocumentSites?.repairCount ?? 0,
  )

  const site = tallyTrees(work.next, { countOptional })

  function othersOf(): OtherSites {
    if (bid === null) return { state: 'none' }
    if (bid.version.sites.length <= 1) return { state: 'ready', sites: [] }
    if (otherDocumentSites !== null) {
      return {
        state: 'ready',
        sites: otherDocumentSites.sites.map((entry) => {
          const tally = tallyTrees(entry.trees, { countOptional })
          return {
            siteId: entry.siteId,
            name: entry.siteName,
            cents: tally.countedCents,
            incomplete: tally.unestimated > 0,
          }
        }),
      }
    }
    return document.isError ? { state: 'unavailable' } : { state: 'loading' }
  }
  const others = othersOf()

  // Unknown until the settings have loaded, which is not the same as unset.
  const settingsLoaded = args.organization !== undefined
  const rateIsMissing = settingsLoaded && ratePerHour === null
  const otherCents =
    others.state === 'ready'
      ? others.sites.reduce((sum, entry) => sum + entry.cents, 0)
      : 0
  const subtotal = site.countedCents + otherCents
  // No rate and nothing priced is no price at all, never $0.00.
  const subtotalCents =
    !settingsLoaded ||
    (rateIsMissing && subtotal === 0 && handPricedOn(args.trees) === 0)
      ? null
      : subtotal
  const taxRatePercent =
    otherDocumentSites?.taxRatePercent ??
    args.organization?.taxRatePercent ??
    null
  const taxed = taxOn(
    subtotalCents === null ? null : formatMoneyValue(subtotalCents),
    taxRatePercent,
  )

  const laterSeasons = work.later.map(({ season, trees }) => {
    const priced = trees.filter(hasLaterCents)
    return {
      season,
      trees: priced.length,
      cents: priced.reduce((sum, tree) => sum + tree.cents, 0),
    }
  })

  const byId = new Map(work.next.map((tree) => [tree.treeId, tree]))
  function priceOf(treeId: string): TreePrice | null {
    const tree = byId.get(treeId)
    const laterParts = work.later.flatMap(({ season, trees }) =>
      trees
        .filter(hasLaterCents)
        .filter((part) => part.treeId === treeId)
        .map((part) => ({ season, cents: part.cents })),
    )
    const [firstLater] = laterParts
    const later =
      firstLater === undefined
        ? null
        : {
            season: firstLater.season,
            cents: laterParts.reduce((sum, part) => sum + part.cents, 0),
          }
    if (tree === undefined) {
      return later === null
        ? null
        : {
            cents: 0,
            choice: 'NONE',
            isOptional: false,
            removalAlternativeCents: null,
            later,
          }
    }
    return {
      cents: tree.cents.totalCents,
      choice: tree.cents.choice,
      isOptional: tree.row.isOptional && !countOptional,
      removalAlternativeCents:
        tree.cents.choice === 'PRUNE' && tree.row.removal !== null
          ? parseMoney(tree.row.removal.amount)
          : null,
      later,
    }
  }

  return {
    bidContext,
    work,
    site,
    others,
    subtotalCents,
    taxRatePercent,
    taxCents: parseMoney(taxed.taxAmount),
    totalCents: parseMoney(taxed.amountDue),
    isIncomplete:
      site.unestimated > 0 ||
      (others.state === 'ready' && others.sites.some((entry) => entry.incomplete)) ||
      (rateIsMissing && site.minutes > 0),
    rateIsMissing,
    handPricedCount: handPricedOn(args.trees),
    laterSeasons,
    siteCents: site.countedCents,
    priceOf,
  }
}
